/**
 * Proyección WGS84 ↔ metros locales para el mapa de sanidad (Fase 5, 2026-08-03).
 *
 * Réplica EXACTA de la fórmula del asistente geométrico
 * (src/greenhouse-designer/steps/PerimeterStep.tsx — wgs84ToLocalMeters):
 * equirectangular centrada en el centroide, Y invertida (coords de pantalla),
 * origen (0,0) = esquina mín del anillo proyectado. Si esa fórmula cambia,
 * ESTA debe cambiar igual o los focos GPS se desplazan (test de vectores en
 * src/__tests__/wgs84Local.test.ts).
 *
 * Vive FUERA del kernel (no editable desde Pest) y FUERA de greenhouse-designer
 * (sincronizado con Tactic) a propósito.
 */

export interface LatLng {
  lat: number;
  lng: number;
}
export interface XY {
  x: number;
  y: number;
}

const METERS_PER_DEG_LAT = 111_320;

/** Proyección cruda (sin normalizar) respecto a un centro dado. */
export function projectRaw(p: LatLng, center: LatLng): XY {
  const metersPerDegLng = METERS_PER_DEG_LAT * Math.cos((center.lat * Math.PI) / 180);
  return {
    x: (p.lng - center.lng) * metersPerDegLng,
    y: -(p.lat - center.lat) * METERS_PER_DEG_LAT,
  };
}

/** Anillo WGS84 de un GeoJSON.Polygon guardado en greenhouses.geometry. */
export function ringFromGeoPolygon(geoPolygon: unknown): LatLng[] {
  const coords = (geoPolygon as { coordinates?: [number, number][][] } | null)?.coordinates?.[0];
  if (!Array.isArray(coords)) return [];
  return coords
    .filter((c) => Array.isArray(c) && c.length >= 2)
    .map(([lng, lat]) => ({ lat, lng }));
}

/**
 * Convierte un punto GPS a las coordenadas LOCALES de un invernadero (el mismo
 * sistema en el que están geometry.perimeter / blocks / block_rows): proyecta
 * con el centroide del invernadero y resta el mín de su anillo proyectado.
 */
export function projectWgs84ToGreenhouseLocal(
  gps: LatLng,
  gh: { centroid: LatLng; ringWgs84: LatLng[] },
): XY | null {
  if (gh.ringWgs84.length < 3) return null;
  const projected = gh.ringWgs84.map((p) => projectRaw(p, gh.centroid));
  const minX = Math.min(...projected.map((p) => p.x));
  const minY = Math.min(...projected.map((p) => p.y));
  const raw = projectRaw(gps, gh.centroid);
  return { x: raw.x - minX, y: raw.y - minY };
}

/**
 * La VUELTA: metros locales → WGS84. Inversa exacta de `projectRaw`.
 *
 * Es lo que permite poner el plano encima de una foto de satélite: la nave
 * está dibujada en metros y las teselas están en la Tierra, así que hace falta
 * saber en qué latitud y longitud cae cada vértice.
 *
 * Se despeja de `projectRaw` y de nada más. Si aquella cambia, esta se rompe, y
 * por eso el test de ida y vuelta las ata: `localToWgs84(projectRaw(p)) === p`.
 */
export function unprojectRaw(local: XY, center: LatLng): LatLng {
  const metersPerDegLng = METERS_PER_DEG_LAT * Math.cos((center.lat * Math.PI) / 180);
  return {
    lat: center.lat - local.y / METERS_PER_DEG_LAT,
    lng: center.lng + local.x / metersPerDegLng,
  };
}

/**
 * Un punto en las coordenadas LOCALES de un invernadero → WGS84.
 *
 * Inversa exacta de `projectWgs84ToGreenhouseLocal`, y con la misma firma al
 * revés: hay que volver a sumar el mínimo del anillo proyectado, porque el
 * origen local (0,0) NO es el centroide sino la esquina mínima de la caja del
 * anillo (así lo dejó `wgs84ToLocalMeters` en el asistente).
 *
 * Devuelve `null` sin anillo válido, igual que la ida: sin anillo no hay
 * origen, y adivinarlo pondría la nave en otro campo.
 */
export function localToWgs84(
  local: XY,
  gh: { centroid: LatLng; ringWgs84: LatLng[] },
): LatLng | null {
  if (gh.ringWgs84.length < 3) return null;
  const projected = gh.ringWgs84.map((p) => projectRaw(p, gh.centroid));
  const minX = Math.min(...projected.map((p) => p.x));
  const minY = Math.min(...projected.map((p) => p.y));
  return unprojectRaw({ x: local.x + minX, y: local.y + minY }, gh.centroid);
}

/**
 * Offset de un invernadero dentro del PLANO COMÚN de la ubicación (proyección
 * centrada en el centroide medio de todas las naves). local + offset = plano
 * común. Devuelve null sin anillo (caller usa layout de respaldo).
 */
export function greenhouseOffsetInPlane(
  ringWgs84: LatLng[],
  commonCenter: LatLng,
): XY | null {
  if (ringWgs84.length < 3) return null;
  const projected = ringWgs84.map((p) => projectRaw(p, commonCenter));
  return {
    x: Math.min(...projected.map((p) => p.x)),
    y: Math.min(...projected.map((p) => p.y)),
  };
}

/**
 * Punto de un segmento a→b más cercano a p, con su fracción t clampada a
 * [0,1]. Misma proyección que snapToNearestEdge del kernel, devolviendo
 * además t (el kernel no lo expone).
 */
export function nearestPointOnSegment(
  p: XY,
  a: XY,
  b: XY,
): { point: XY; t: number; distance: number } {
  const dx = b.x - a.x;
  const dy = b.y - a.y;
  const len2 = dx * dx + dy * dy;
  const t = len2 === 0 ? 0 : Math.max(0, Math.min(1, ((p.x - a.x) * dx + (p.y - a.y) * dy) / len2));
  const point = { x: a.x + t * dx, y: a.y + t * dy };
  return { point, t, distance: Math.hypot(p.x - point.x, p.y - point.y) };
}

/** Ray casting clásico sobre un anillo en metros locales. */
export function pointInRing(p: XY, ring: XY[]): boolean {
  let inside = false;
  for (let i = 0, j = ring.length - 1; i < ring.length; j = i++) {
    const a = ring[i], b = ring[j];
    if (a.y > p.y !== b.y > p.y && p.x < ((b.x - a.x) * (p.y - a.y)) / (b.y - a.y) + a.x) {
      inside = !inside;
    }
  }
  return inside;
}

/** Distancia (m) del punto al borde del anillo. 0 si el anillo es inválido. */
export function distanceToRing(p: XY, ring: XY[]): number {
  if (ring.length < 2) return 0;
  let best = Infinity;
  for (let i = 0; i < ring.length; i++) {
    const { distance } = nearestPointOnSegment(p, ring[i], ring[(i + 1) % ring.length]);
    if (distance < best) best = distance;
  }
  return best;
}

// Regla del perímetro: fuera creíble ⟺ distancia − precisión > 100 m.
// OJO, deliberadamente DISTINTA del geofence de Tactic: allí el techo de
// crédito (50 m) protege un gate BLOQUEANTE contra "cuanto peor el aparato,
// más indulgencia compra". Aquí el veredicto se usa para DESACREDITAR un fix
// (degradar el ancla y avisar al trabajador), así que la precisión abona
// entera: con un fix de ±300 m no se puede afirmar que alguien a 160 m está
// fuera. Precisión desconocida ⇒ nunca se acusa (hallazgo adversarial
// 2026-08-04). Quien SÍ está dentro tiene distancia 0 y pasa siempre.
const PERIMETER_MARGIN_M = 100;

/**
 * ¿El fix GPS sitúa al trabajador CREÍBLEMENTE fuera del invernadero? Solo
 * con un "sí" rotundo se degrada el anclaje (el reporte nunca se bloquea).
 * `local` = GPS proyectado al plano local de la nave; `ring` = geometry.perimeter.
 */
export function isCrediblyOutsidePerimeter(
  local: XY,
  ring: XY[],
  accuracyM: number | null,
): boolean {
  if (ring.length < 3) return false;
  if (pointInRing(local, ring)) return false;
  const credit = accuracyM ?? Number.POSITIVE_INFINITY;
  return distanceToRing(local, ring) - credit > PERIMETER_MARGIN_M;
}
